import { useState } from "react";
import { api, API } from "@/lib/api";
import ReferencePicker from "@/components/ReferencePicker";

/** Modal that talks to AI for one dossier section. Returns text + insert/replace.
 *  Props:
 *  - projectId
 *  - section: { key, label, question }
 *  - currentText
 *  - onClose
 *  - onApply(text, mode)   mode = "replace" | "append"
 */
export default function AiAssistantModal({ projectId, section, currentText, onClose, onApply }) {
  const [tab, setTab] = useState(currentText ? "rewrite" : "generate");
  const [brief, setBrief] = useState("");
  const [instruction, setInstruction] = useState("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [referenceIds, setReferenceIds] = useState([]);

  const run = async () => {
    setLoading(true);
    setError("");
    setDraft("");
    try {
      const url = tab === "generate"
        ? `/icaa/projects/${projectId}/ai/section`
        : `/icaa/projects/${projectId}/ai/rewrite`;
      const payload = tab === "generate"
        ? { section_key: section.key, brief, reference_ids: referenceIds }
        : { section_key: section.key, current_text: currentText, instruction, reference_ids: referenceIds };
      const r = await api.post(url, payload, { timeout: 90000 });
      setDraft(r.data.text || "");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const QUICK = tab === "generate" ? [
    "Tono más literario y evocador.",
    "Más corto, 2 párrafos máximo.",
    "Más cinematográfico, con imágenes concretas.",
    "Subraya el conflicto central.",
  ] : [
    "Más corto.",
    "Más poético.",
    "Más directo y concreto.",
    "Cambia el orden de los párrafos.",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 p-4" onClick={onClose} data-testid="ai-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-[#FAFAF6] shadow-2xl">
        <header className="flex items-center justify-between border-b border-stone-300/60 px-6 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#26775E]">Asistente · Claude Sonnet 4.5</p>
            <h2 className="mt-0.5 font-serif text-2xl">{section.label}</h2>
            <p className="text-xs italic text-stone-500">{section.question}</p>
          </div>
          <button onClick={onClose} data-testid="ai-modal-close"
                  className="rounded-full p-2 text-stone-500 hover:bg-stone-200 hover:text-stone-900">×</button>
        </header>

        <div className="flex gap-1 border-b border-stone-300/60 bg-stone-100 px-6 py-2">
          <TabBtn label="Generar" active={tab === "generate"} onClick={() => setTab("generate")} testid="ai-tab-generate" />
          <TabBtn label="Reescribir" active={tab === "rewrite"} disabled={!currentText}
                  onClick={() => setTab("rewrite")} testid="ai-tab-rewrite" />
        </div>

        <div className="grid grid-cols-1 gap-0 md:grid-cols-2">
          {/* Input panel */}
          <div className="border-r border-stone-300/60 p-6">
            {tab === "generate" ? (
              <>
                <label className="text-xs uppercase tracking-widest text-stone-500">Brief opcional</label>
                <textarea value={brief} onChange={(e) => setBrief(e.target.value)} data-testid="ai-brief"
                          rows={6} placeholder="Ej: Quiero que mencione la pesca submarina, que sea cinemográfico, máximo 3 párrafos…"
                          className="mt-2 w-full rounded-md border border-stone-300 bg-white p-3 text-sm outline-none focus:border-[#26775E]" />
              </>
            ) : (
              <>
                <label className="text-xs uppercase tracking-widest text-stone-500">Instrucción para reescribir</label>
                <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} data-testid="ai-instruction"
                          rows={6} placeholder="Ej: Más poético, conserva la última frase…"
                          className="mt-2 w-full rounded-md border border-stone-300 bg-white p-3 text-sm outline-none focus:border-[#26775E]" />
              </>
            )}

            <div className="mt-3 flex flex-wrap gap-1">
              {QUICK.map((q) => (
                <button key={q} onClick={() => tab === "generate" ? setBrief((b) => (b ? b + " " : "") + q) : setInstruction((i) => (i ? i + " " : "") + q)}
                        className="rounded-full bg-stone-200 px-3 py-1 text-[11px] text-stone-700 hover:bg-stone-300">
                  + {q}
                </button>
              ))}
            </div>

            <div className="mt-4">
              <ReferencePicker value={referenceIds} onChange={setReferenceIds} />
            </div>

            <button onClick={run} disabled={loading} data-testid="ai-run-btn"
                    className="mt-5 w-full rounded-full bg-stone-900 px-6 py-3 text-sm uppercase tracking-widest text-[#FAFAF6] transition hover:bg-[#26775E] disabled:opacity-50">
              {loading ? "Pensando…" : tab === "generate" ? "Generar borrador" : "Reescribir"}
            </button>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          </div>

          {/* Output panel */}
          <div className="flex flex-col p-6">
            <label className="text-xs uppercase tracking-widest text-stone-500">Borrador propuesto</label>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="ai-draft"
                      rows={14} placeholder="Aquí aparecerá el texto generado. Puedes editarlo antes de insertar."
                      className="mt-2 min-h-[260px] flex-1 rounded-md border border-stone-300 bg-white p-3 font-serif text-[13px] leading-relaxed outline-none focus:border-[#26775E]" />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => onApply(draft, "append")} disabled={!draft.trim()} data-testid="ai-append"
                      className="rounded-full border border-stone-400 px-4 py-2 text-xs uppercase tracking-widest text-stone-700 hover:border-[#26775E] disabled:opacity-50">
                Añadir al final
              </button>
              <button onClick={() => onApply(draft, "replace")} disabled={!draft.trim()} data-testid="ai-replace"
                      className="rounded-full bg-[#26775E] px-4 py-2 text-xs uppercase tracking-widest text-white hover:bg-stone-900 disabled:opacity-50">
                Reemplazar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ label, active, onClick, disabled, testid }) => (
  <button disabled={disabled} onClick={onClick} data-testid={testid}
          className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${
            active ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600 hover:bg-stone-200"
          } disabled:opacity-40 disabled:cursor-not-allowed`}>
    {label}
  </button>
);
